/* eslint-disable jsx-a11y/no-static-element-interactions */
import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useDrag } from 'react-dnd';
import get from 'lodash/get';
import { ButtonWithIcon } from '@els/els-react--button';
import { Flyout } from '@els/els-react--flyout';
import UserDatabases from '@Services/UserDatabases';
import { useDispatch, useSelector } from 'react-redux';
import { ValidatedTextInput } from '@els/els-react--text-input';
import { deleteUserFormAction, saveUserFormAction } from '../../QueryletActions';
import { forms } from '../../QuerySelector';

import { useTargetMode } from '../../TargetModeContext';

import './FormDragNode.scss';

function FormDragNode({ node, addToBuilder }) {
  const dispatch = useDispatch();
  const { activateTargetMode, canActivateTargetMode, registerSource } = useTargetMode();
  const sourceId = `side-panel:${node.uuid || node.id}`;
  const canChooseGroupingTarget = canActivateTargetMode({ kind: 'side-panel', node });
  const [isKeyboardTooltipOpen, setIsKeyboardTooltipOpen] = useState(false);
  const [isEdit, setEdit] = useState(false);
  const [isAdded, setIsAdded] = useState(false);
  const [displayName, setDisplayName] = useState(node.displayName);
  const addedTimerRef = useRef(null);
  const formList = useSelector(forms);

  const triggerAddedState = () => {
    setIsAdded(true);
    if (addedTimerRef.current) {
      clearTimeout(addedTimerRef.current);
    }
    addedTimerRef.current = setTimeout(() => {
      setIsAdded(false);
      addedTimerRef.current = null;
    }, 1000);
  };
  const [{ isDragging }, dragRef] = useDrag(
    {
      type: 'DragNode',
      item: { node, type: 'DragNode' },
      collect: (monitor) => ({
        isDragging: monitor.isDragging(),
      }),
      end: (_item, monitor) => {
        if (monitor.didDrop()) {
          triggerAddedState();
        }
      },
    },
    [node]
  );

  useEffect(() => {
    setDisplayName(node.displayName);
  }, [node]);

  useEffect(() => {
    if (isDragging) document.body.style.cursor = 'move !important';
    else document.body.style.cursor = 'normal';
  }, [isDragging]);

  useEffect(
    () => () => {
      if (addedTimerRef.current) {
        clearTimeout(addedTimerRef.current);
      }
    },
    []
  );

  const handleAddToBuilder = (n) => {
    addToBuilder(n);
    triggerAddedState();
  };

  const isFormNameUnique = () => {
    const myFormsList = formList.filter((formItem) => formItem.displayName === 'My Forms')[0];
    const trimmedDisplayName = displayName.trim();

    return (
      get(myFormsList, 'children', []).filter((child) => child.displayName.trim() === trimmedDisplayName && child.formId !== node.formId)
        .length === 0
    );
  };

  const editUserFormName = () => {
    setEdit(!isEdit);
    if (!displayName || displayName.trim().length < 3 || displayName.trim().length > 20 || !isFormNameUnique()) {
      setEdit(true);
    } else {
      dispatch(saveUserFormAction(displayName.trim(), node));
    }
  };

  const onDeleteClick = () => {
    setEdit(false);
    dispatch(deleteUserFormAction(node));
  };

  return (
    <div className="query-builder-search-section__drag-node" key={`form-${node.uuid}`}>
      <div className="query-builder-search-section__drag-node-content">
        {isEdit && (
          <div>
            <ValidatedTextInput
              id={`editName-${node.id}`}
              className={isFormNameUnique() ? '' : 'c-els-field--error'}
              value={displayName}
              label={displayName}
              data-e2e="edit-form-name"
              min={3}
              max={20}
              shouldHideLabel
              required
              validator="none"
              style={{ maxWidth: '9rem' }}
              onChange={(event) => setDisplayName(event.target.value)}
            />
            {!isFormNameUnique() && (
              <div aria-live="assertive" aria-atomic="true" className="c-els-field__message c-els-field__message--error">
                <span className="c-els-field__message-icon icon__wrapper u-els-color-warn">Please enter a unique form name</span>
              </div>
            )}
          </div>
        )}
        {!isEdit && (
          <ButtonWithIcon
            type="link"
            sprite="Layers"
            linkIconSize="s"
            data-e2e="layersIcon"
            className={node.type === 'form' ? 'group-name' : ''}
            onClick={(e) => {
              handleAddToBuilder(node);
              e.stopPropagation();
            }}>
            {node.displayName}
          </ButtonWithIcon>
        )}
        <div style={{ display: 'flex' }}>
          {node.databases && node.databases.length > 1 && (
            <Flyout
              flyout={
                <>
                  <h4>Databases</h4>
                  <div className="query-builder-search-section__drag-node-db">
                    {node.databases.map((option) => (
                      <ButtonWithIcon
                        key={option}
                        type="link"
                        sprite="Database"
                        data-e2e="databases-button"
                        onClick={(event) => event.stopPropagation()}>
                        {UserDatabases.getDbName(option)}
                      </ButtonWithIcon>
                    ))}
                  </div>
                </>
              }
              shouldCloseOnESC>
              <ButtonWithIcon
                type="link"
                size="small"
                linkIconSize="s"
                iconOnly
                data-e2e="databasesIcon"
                sprite="Database"
                onClick={(event) => event.stopPropagation()}>
                {node.databases[0]}
              </ButtonWithIcon>
            </Flyout>
          )}
          {node.editMode && (
            <>
              <Flyout
                trigger="hover"
                className="qb-drag-node-icon"
                flyout={<span aria-hidden="true">{isEdit ? 'Save' : 'Edit group name'}</span>}
                placement="bottom"
                shouldCloseOnESC
                theme="simple">
                <ButtonWithIcon
                  id={`edit-${node.id}`}
                  iconOnly
                  type="link"
                  linkIconSize="s"
                  size="small"
                  data-e2e="editFormNameButton"
                  sprite={isEdit ? 'Checkmark' : 'Edit'}
                  aria-label={isEdit ? 'Save' : 'Edit group name'}
                  onClick={() => editUserFormName()}>
                  {isEdit ? 'Save Edited Form' : 'Edit Form'}
                </ButtonWithIcon>
              </Flyout>
              <Flyout
                trigger="hover"
                className="qb-drag-node-icon"
                flyout={<span aria-hidden="true">Delete form</span>}
                placement="bottom"
                shouldCloseOnESC
                theme="simple">
                <ButtonWithIcon
                  id={`delete-${node.id}`}
                  iconOnly
                  type="link"
                  linkIconSize="s"
                  data-e2e="deleteFormButton"
                  size="small"
                  style={{ padding: '0 .5rem' }}
                  sprite="Trash"
                  aria-label="Delete form"
                  onClick={() => onDeleteClick()}>
                  Delete Form
                </ButtonWithIcon>
              </Flyout>
            </>
          )}
          <div aria-live="polite" aria-atomic="true">
            {isDragging && <span className="sr-only">{node.displayName} is being dragged to query builder</span>}
            {isAdded && <span className="sr-only">{node.displayName} added to query builder</span>}
          </div>
          <div ref={dragRef}>
            <Flyout
              trigger="hover"
              isControlled
              isOpen={isKeyboardTooltipOpen}
              onToggle={() => {}}
              shouldCloseOnESC
              flyout={canChooseGroupingTarget ? 'Drag or press Enter to choose a grouping target' : 'Drag or click to add to query builder'}
              placement="bottom"
              theme="simple">
              <ButtonWithIcon
                id={`querybuilder-sidepanel-dragform-${node.uuid || node.id}`}
                ref={(element) => registerSource(sourceId, element)}
                draggable
                data-e2e="querybuilder-dragform"
                iconOnly
                className="cursor-move"
                linkIconSize="xxs"
                sprite="Drag"
                type="borderless"
                aria-label={
                  canChooseGroupingTarget ? `Choose grouping target for ${node.displayName}` : `Add ${node.displayName} to query builder`
                }
                onFocus={(event) => setIsKeyboardTooltipOpen(event.currentTarget.matches(':focus-visible'))}
                onBlur={() => setIsKeyboardTooltipOpen(false)}
                // Activation lives on click: a native button turns Enter/Space into a click, and
                // screen readers activate by emulating a click (possibly a real one, so detail
                // cannot be filtered on). An HTML5 drag never fires click, so drag is unaffected.
                // With nothing in the tree to group against, fall back to a plain add.
                onClick={() => {
                  if (!canChooseGroupingTarget || !activateTargetMode({ kind: 'side-panel', node, sourceId })) {
                    handleAddToBuilder(node);
                  }
                }}>
                drag
              </ButtonWithIcon>
            </Flyout>
          </div>
        </div>
      </div>
    </div>
  );
}

FormDragNode.propTypes = {
  node: PropTypes.object,
  addToBuilder: PropTypes.func,
};

export default FormDragNode;
